import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { Box, Collapse, IconButton, Stack } from '@mui/material'
import { red, orange, blue, grey } from '@mui/material/colors'
import { type FC, useState } from 'react'
import { formatRelativeTime } from 'shared/utils'

export interface LogRow {
    id: string
    logId: string
    level: string
    message: string
    details?: unknown
    conversationId?: string | null
    createdAt: string | Date
}

interface LogPanelProps {
    logs: LogRow[]
    emptyText?: string
    onConversationClick?: (conversationId: string) => void
}

const LEVEL_COLOR: Record<string, string> = {
    error: red[700],
    warn: orange[700],
    info: blue[700],
    debug: grey[600],
}

export const LogPanel: FC<LogPanelProps> = ({
    logs,
    emptyText = 'No logs.',
    onConversationClick,
}) => {
    if (!logs.length) {
        return (
            <Box
                sx={{
                    p: 2,
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    color: 'text.secondary',
                }}
            >
                {emptyText}
            </Box>
        )
    }
    return (
        <Stack
            divider={
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />
            }
        >
            {logs.map((log) => (
                <LogItem
                    key={log.id}
                    log={log}
                    onConversationClick={onConversationClick}
                />
            ))}
        </Stack>
    )
}

interface LogItemProps {
    log: LogRow
    onConversationClick?: (conversationId: string) => void
}

const LogItem: FC<LogItemProps> = ({ log, onConversationClick }) => {
    const [expanded, setExpanded] = useState(false)
    const hasDetails =
        log.details != null &&
        (Array.isArray(log.details)
            ? (log.details as unknown[]).length > 0
            : typeof log.details === 'object' &&
              Object.keys(log.details as Record<string, unknown>).length > 0)
    const timestamp = new Date(log.createdAt)
    const levelColor = LEVEL_COLOR[log.level] ?? grey[600]

    return (
        <Box
            sx={{ p: 1, fontSize: '0.8rem' }}
            data-testid={`bot-audit-log-row-${log.id}`}
        >
            <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                    sx={{
                        width: 90,
                        fontSize: '0.7rem',
                        color: 'text.secondary',
                    }}
                    title={timestamp.toLocaleString()}
                >
                    {formatRelativeTime(timestamp)}
                </Box>
                <Box
                    sx={{
                        px: 0.75,
                        py: 0.1,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        bgcolor: levelColor,
                        color: 'common.white',
                        borderRadius: 0.5,
                        minWidth: 40,
                        textAlign: 'center',
                    }}
                >
                    {log.level}
                </Box>
                <Box
                    sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.7rem',
                        color: 'text.secondary',
                        minWidth: 100,
                    }}
                >
                    {log.logId}
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {log.message}
                </Box>
                {log.conversationId && onConversationClick ? (
                    <Box
                        component="button"
                        onClick={() => onConversationClick(log.conversationId!)}
                        sx={{
                            fontSize: '0.7rem',
                            color: 'primary.main',
                            bgcolor: 'transparent',
                            border: 0,
                            cursor: 'pointer',
                            p: 0,
                            textDecoration: 'underline',
                        }}
                        data-testid="bot-audit-log-open-thread"
                    >
                        thread
                    </Box>
                ) : null}
                {hasDetails ? (
                    <IconButton
                        size="small"
                        sx={{ p: 0.25 }}
                        onClick={() => setExpanded((e) => !e)}
                        aria-expanded={expanded}
                        aria-label={
                            expanded ? 'Collapse details' : 'Expand details'
                        }
                    >
                        {expanded ? (
                            <ExpandLess fontSize="small" />
                        ) : (
                            <ExpandMore fontSize="small" />
                        )}
                    </IconButton>
                ) : null}
            </Stack>
            {hasDetails ? (
                <Collapse in={expanded}>
                    <Box
                        component="pre"
                        sx={{
                            mt: 0.5,
                            ml: 11,
                            fontSize: '0.7rem',
                            fontFamily: 'monospace',
                            color: 'text.secondary',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                        }}
                    >
                        {JSON.stringify(log.details, null, 2)}
                    </Box>
                </Collapse>
            ) : null}
        </Box>
    )
}
